// statusAuswahlKatalog.ts — die Auswahl unter „Sonstige", als geordnete Liste
// von Einträgen. Löst die frühere Gruppierung nach Themen („Verbindung zu
// Courté", „Umfeld des Kunden" …) ab.
//
// Die Vorgabe vom 14.09.2026: Die Liste folgt der Aufzählung des Users, und
// MANCHE Einträge haben Details. Ein Eintrag mit Details ist NICHT selbst
// wählbar — er ist nur die Überschrift, wählbar sind die Zeilen darunter.
// Zwischen den schlichten Einträgen und jedem Überschrift-Block steht ein
// Trenner (`RollenAuswahl`).
//
// Warum das hier und nicht als weitere Themen-Gruppe: Die alte Einteilung
// beantwortete die Frage „Was ist der Kontakt für wen?". Die neue beantwortet
// „Was ist er — und wenn es dafür Ausprägungen gibt, welche?". Beides
// nebeneinander hieße, denselben Eintrag zweimal unterzubringen.
//
// Die `value`-Strings sind zugleich `ContactStatus.status` und müssen
// ZEICHENGENAU zu `apps/api/src/contactStatus.katalog.ts` passen —
// `ContactService.resolveRelations` verwirft einen unbekannten Namen ERSATZLOS.
// Überschriften haben bewusst KEINEN Wert: Sie erreichen `statusName` nie.

import type { VerantOption } from "../components/verantwortlichkeit/verantwortlichkeitDropdownLogik";
import { STATUS_AP_PRODUKTPARTNER } from "./produktpartnerKatalog";
import {
  STATUS_ALTINTERESSENT,
  STATUS_ALTKUNDE,
  STATUS_BEZIEHUNG,
  STATUS_EMPFEHLUNGSGEBER,
  STATUS_HAUSHALTSMITGLIED,
  STATUS_IMMOBILIENBEZOGEN,
  STATUS_LIEFERANT_DIENSTLEISTER,
  STATUS_MENSCH_IM_UNTERNEHMEN,
  STATUS_MFU,
  STATUS_MITARBEITER_COURTE,
  STATUS_SPONSOR_SPENDER,
  STATUS_VEREIN_VERBAND,
  STATUS_VERMITTLER,
} from "./sonstigeStatusKatalog";

/**
 * Wen eine Rolle als Gegenüber braucht.
 *
 * „Verwalter" allein ist keine Aussage — Verwalter WOVON? Rollen mit dieser
 * Angabe fragen deshalb nach dem Bezugskontakt: suchen oder neu anlegen, bei
 * einer Firma mit Internet-Adresse und „Daten befüllen".
 *
 * `"firma"` und `"person"` legen den Typ fest, den die Anlage vorschlägt;
 * `"kontakt"` lässt beides zu.
 */
export type BezugsArt = "firma" | "person" | "kontakt";

export type StatusDetail = {
  value: string;
  label: string;
  /** Gesetzt = die Rolle verlangt ein Gegenüber. */
  bezug?: BezugsArt;
  /**
   * Gesetzt = zum Gegenüber gehört eine Verwandtschaftsangabe („Tochter",
   * „Ehefrau"), und der Bezug wird als HAUSHALTS-Beziehung gespeichert.
   *
   * Der Wert sagt zugleich, ob die Person im selben Haushalt lebt. Das ist
   * keine zusätzliche Frage: Die Rolle selbst beantwortet sie schon —
   * „Im selben Haushalt" bzw. „Beziehung außerhalb des Haushaltes".
   *
   * Ohne die Verwandtschaft ginge die Beziehung beim Speichern VERLOREN:
   * `persistRelations` verwirft eine Haushaltszeile ohne `relationType`
   * ersatzlos. Deshalb ist das Feld Pflicht, sobald es angeboten wird.
   */
  haushalt?: { verwandtschaft: true; imHaushalt: boolean };
};

export type StatusEintrag = {
  /** Beschriftung. Bei einem Eintrag mit Details zugleich die Überschrift. */
  label: string;
  /** Der gespeicherte Wert. Fehlt genau dann, wenn `details` gesetzt ist. */
  value?: string;
  /** Ausprägungen. Sind sie da, ist der Eintrag selbst nicht wählbar. */
  details?: StatusDetail[];
};

/* ------------------------------------------------------------------ *
 * Bausteine, die mehrfach vorkommen
 * ------------------------------------------------------------------ */

/**
 * Dienstleister, die auch im Schaden auftreten.
 *
 * Sie stehen absichtlich in ZWEI Blöcken — unter „Lieferant/Dienstleister"
 * (weil sie das sind) und unter „Schaden-Beteiligter" (weil man sie dort
 * sucht). Es ist derselbe Wert: Ein Haken in einem Block setzt auch den
 * anderen. Das ist gewollt, die Alternative wären zwei Status für dieselbe
 * Firma.
 */
const SCHADEN_DIENSTLEISTER: StatusDetail[] = [
  { value: "Reparaturfirma", label: "Reparaturfirma", bezug: "kontakt" },
  { value: "Rechtsanwalt im Schaden", label: "Rechtsanwalt im Schaden", bezug: "kontakt" },
  { value: "Sachverständiger", label: "Sachverständiger", bezug: "kontakt" },
];

/**
 * Die EXTERNEN Zuständigkeiten — dieselben Rollen, die ein Unternehmen intern
 * besetzt, nur von außen erbracht (Auftrag vom 13.09.2026: „das sind aber dann
 * externe").
 *
 * Die Namen sind die des Bestands (`202606090001-ReworkVerantwortlichkeit`,
 * Spalte `art='extern'`) und wurden ausdrücklich NICHT begradigt: Extern heißt
 * die Steuer-Zuständigkeit „Steuerberater" und nicht „Steuern", die
 * IT-Zuständigkeit „IT-Dienstleister" und nicht „IT-Verantwortlicher". Das sind
 * die Berufsbezeichnungen, unter denen man sie sucht.
 *
 * „Prokurist" fehlt hier mit Absicht: Prokura ist eine Organrolle (Bereich
 * „Vertretung") und per Definition intern — ein Prokurist ist kein externer
 * Dienstleister.
 */
const EXTERNE_ZUSTAENDIGKEITEN: StatusDetail[] = [
  { value: "Arbeitsschutzbeauftragter", label: "Arbeitsschutzbeauftragter", bezug: "firma" },
  { value: "Brandschutzbeauftragter", label: "Brandschutzbeauftragter", bezug: "firma" },
  { value: "Compliance-Beauftragter", label: "Compliance-Beauftragter", bezug: "firma" },
  { value: "Datenschutzbeauftragter", label: "Datenschutzbeauftragter", bezug: "firma" },
  { value: "Finanz-Buchhaltung", label: "Finanz-Buchhaltung", bezug: "firma" },
  { value: "Fuhrparkmanager", label: "Fuhrparkmanager", bezug: "firma" },
  { value: "Gebäudemanager", label: "Gebäudemanager", bezug: "firma" },
  { value: "HR (Menschen im Unternehmen)", label: "HR (Menschen im Unternehmen)", bezug: "firma" },
  { value: "IT-Dienstleister", label: "IT-Dienstleister", bezug: "firma" },
  { value: "Lohn-Buchhaltung", label: "Lohn-Buchhaltung", bezug: "firma" },
  { value: "Notar", label: "Notar", bezug: "firma" },
  { value: "Qualitätsmanagement", label: "Qualitätsmanagement", bezug: "firma" },
  { value: "Rechtsanwalt", label: "Rechtsanwalt", bezug: "firma" },
  { value: "Sicherheitsbeauftragter", label: "Sicherheitsbeauftragter", bezug: "firma" },
  { value: "Steuerberater", label: "Steuerberater", bezug: "firma" },
  { value: "Transport & Logistik", label: "Transport & Logistik", bezug: "firma" },
  { value: "Versicherungen (Schäden)", label: "Versicherungen (Schäden)", bezug: "firma" },
  { value: "Versicherungen (Verträge)", label: "Versicherungen (Verträge)", bezug: "firma" },
  { value: "Wirtschaftsprüfer", label: "Wirtschaftsprüfer", bezug: "firma" },
  { value: "Sonstiger Dienstleister", label: "Sonstiger Dienstleister", bezug: "firma" },
];

/** Rollen an einem Objekt — wer es besitzt, betreibt, nutzt, least. */
const OBJEKTROLLEN: StatusDetail[] = [
  { value: "Eigentümer", label: "Eigentümer (Anteil in %)", bezug: "kontakt" },
  { value: "Betreiber", label: "Betreiber", bezug: "kontakt" },
  { value: "Nutzer", label: "Nutzer", bezug: "kontakt" },
  { value: "Verwalter", label: "Verwalter", bezug: "kontakt" },
  { value: "Leasingnehmer", label: "Leasingnehmer", bezug: "kontakt" },
  { value: "Leasinggeber", label: "Leasinggeber", bezug: "kontakt" },
];

/** Wer an einer Immobilie ein Recht hat. „Verwalter" steht auch oben. */
const IMMOBILIENROLLEN: StatusDetail[] = [
  { value: "Verwalter", label: "Verwalter", bezug: "kontakt" },
  { value: "Mieter", label: "Mieter", bezug: "kontakt" },
  { value: "Vermieter", label: "Vermieter", bezug: "kontakt" },
  { value: "Pächter", label: "Pächter", bezug: "kontakt" },
  { value: "Verpächter", label: "Verpächter", bezug: "kontakt" },
  { value: "Erbbauberechtigter", label: "Erbbauberechtigter", bezug: "kontakt" },
  { value: "Erbbaurechtsgeber", label: "Erbbaurechtsgeber", bezug: "kontakt" },
  { value: "Nießbrauchberechtigter", label: "Nießbrauchberechtigter", bezug: "kontakt" },
  { value: "Nießbrauchgeber", label: "Nießbrauchgeber", bezug: "kontakt" },
  { value: "Zwangsverwalter", label: "Zwangsverwalter", bezug: "kontakt" },
];

/**
 * Rollen im Schaden.
 *
 * Die ersten drei entstehen laut PO ausschließlich am Schaden selbst; sie
 * stehen hier trotzdem, weil die Excel sie führt und der User sie am
 * 11.09.2026 so bestellt hat.
 */
const SCHADENROLLEN: StatusDetail[] = [
  { value: "Anspruchsteller", label: "Anspruchsteller" },
  { value: "Geschädigter", label: "Geschädigter" },
  { value: "Schadenverursacher", label: "Schadenverursacher" },
  { value: "Halter", label: "Halter" },
  ...SCHADEN_DIENSTLEISTER,
  { value: "Regulierer VR", label: "Regulierer VR" },
  { value: "Sachbearbeiter VR", label: "Sachbearbeiter VR" },
  { value: "sonstiger Dienstleister VR", label: "sonstiger Dienstleister VR" },
  { value: "Immobilienmieter", label: "Immobilienmieter" },
];

/** Beteiligungsarten, direkt und indirekt. */
const BETEILIGUNGSROLLEN: StatusDetail[] = [
  { value: "Beteiligung an", label: "Beteiligung an", bezug: "firma" },
  { value: "Muttergesellschaft von", label: "Muttergesellschaft von", bezug: "firma" },
  { value: "Tochtergesellschaft von", label: "Tochtergesellschaft von", bezug: "firma" },
  { value: "Schwestergesellschaft von", label: "Schwestergesellschaft von", bezug: "firma" },
  { value: "Joint Venture mit", label: "Joint Venture mit", bezug: "firma" },
  {
    value: "Beteiligung außerhalb des Firmenverbundes an",
    label: "Beteiligung außerhalb des Firmenverbundes an",
    bezug: "firma",
  },
];

/* ------------------------------------------------------------------ *
 * Die Liste der FIRMA
 * ------------------------------------------------------------------ */

/**
 * Zwölf Einträge in der Reihenfolge der Aufzählung vom 14.09.2026. Vier davon
 * haben Details und sind damit reine Überschriften.
 */
export const STATUS_EINTRAEGE_FIRMA: StatusEintrag[] = [
  { value: "Altkunde", label: "Altkunde" },
  { value: "Altinteressent", label: "Altinteressent" },
  {
    value: "Arbeitgeber eines Privat-Kontaktes",
    label: "Arbeitgeber eines Privat-Kontaktes",
  },
  { value: "Vermittler, Tippgeber", label: "Vermittler/Tippgeber (kann Provision erhalten)" },
  { value: "Empfehlungsgeber", label: "Empfehlungsgeber (keine Provision)" },
  { value: "Sponsor/Spender", label: "Sponsor/Spender" },
  {
    value: "Verein/Verband",
    label: "Verein/Verband (Mitgliedschaft/Verantwortung)",
  },
  { value: "Produktpartner", label: "Produktpartner" },
  { label: "Beteiligung, direkt und indirekt", details: BETEILIGUNGSROLLEN },
  {
    label: "Lieferant/Dienstleister",
    details: [...OBJEKTROLLEN, ...SCHADEN_DIENSTLEISTER, ...EXTERNE_ZUSTAENDIGKEITEN],
  },
  { label: "Immobilienbezogener Kontakt", details: IMMOBILIENROLLEN },
  { label: "Schaden-Beteiligter", details: SCHADENROLLEN },
];

/* ------------------------------------------------------------------ *
 * Die Liste der PERSON
 * ------------------------------------------------------------------ */

/**
 * Beziehung zu einem anderen Privatkontakt.
 *
 * Der User am 13.09.2026: „muss man dann noch angeben, mit wem man die
 * Beziehung hat, dann Frage im Haushalt oder nicht". Beides steckt hier drin —
 * das WER kommt aus der Rückfrage, das OB aus der gewählten Zeile. Eine dritte
 * Angabe braucht es nicht.
 *
 * Die Werte sind die des Bestands und dürfen sich nicht ändern: An
 * `Haushaltsmitglied` hängen die Haushalts-Gruppe und die geerbte Wohnanschrift.
 */
const BEZIEHUNGSROLLEN: StatusDetail[] = [
  {
    value: STATUS_HAUSHALTSMITGLIED,
    label: "Im selben Haushalt",
    bezug: "person",
    haushalt: { verwandtschaft: true, imHaushalt: true },
  },
  {
    value: STATUS_BEZIEHUNG,
    label: "Beziehung außerhalb des Haushaltes",
    bezug: "person",
    haushalt: { verwandtschaft: true, imHaushalt: false },
  },
];

/**
 * Die Verbindung einer Person zu einem Unternehmen.
 *
 * Auftrag vom 11.09.2026: „ja mensch fürs unternehmen und im unternehmen unter
 * arbeitgeber". Beide Zeilen fragen nach der Firma — ohne sie ist die Rolle
 * keine Aussage.
 */
const UNTERNEHMENSROLLEN: StatusDetail[] = [
  {
    value: STATUS_MENSCH_IM_UNTERNEHMEN,
    label: `${STATUS_MENSCH_IM_UNTERNEHMEN} (Arbeitnehmer)`,
    bezug: "firma",
  },
  {
    value: STATUS_MFU,
    label: STATUS_MFU,
    bezug: "firma",
  },
];

/**
 * Die Liste der PERSON — dieselbe Ordnung wie bei der Firma: erst die
 * schlichten Einträge in einem Block ohne Überschrift, dann je ein Block mit
 * Überschrift und Trenner für die Einträge mit Details.
 *
 * Die Rollen unter „Lieferant/Dienstleister" und „Immobilienbezogener Kontakt"
 * sind DIESELBEN wie bei der Firma — ein Sachverständiger heißt so, ob er als
 * Person oder als Büro auftritt. Der Auftrag vom 13.09.2026 war ausdrücklich,
 * die Rollen zu teilen und „genau so wie bisher" zu benennen.
 *
 * „Schaden-Beteiligter" fehlt hier bewusst: Der User hat ihn am 11.09.2026 für
 * die Person ausgenommen.
 */
export const STATUS_EINTRAEGE_PERSON: StatusEintrag[] = [
  { value: STATUS_ALTKUNDE, label: STATUS_ALTKUNDE },
  { value: STATUS_ALTINTERESSENT, label: STATUS_ALTINTERESSENT },
  { value: STATUS_VERMITTLER, label: `${STATUS_VERMITTLER} (kann Provision erhalten)` },
  { value: STATUS_EMPFEHLUNGSGEBER, label: `${STATUS_EMPFEHLUNGSGEBER} (keine Provision)` },
  { value: STATUS_AP_PRODUKTPARTNER, label: STATUS_AP_PRODUKTPARTNER },
  { value: STATUS_SPONSOR_SPENDER, label: STATUS_SPONSOR_SPENDER },
  { value: STATUS_VEREIN_VERBAND, label: `${STATUS_VEREIN_VERBAND} (Mitgliedschaft/Verantwortung)` },
  { value: STATUS_MITARBEITER_COURTE, label: STATUS_MITARBEITER_COURTE },
  { label: "Beziehung zu Privatkontakten", details: BEZIEHUNGSROLLEN },
  { label: "Unternehmens-Verbindung", details: UNTERNEHMENSROLLEN },
  {
    label: STATUS_LIEFERANT_DIENSTLEISTER,
    details: [...OBJEKTROLLEN, ...SCHADEN_DIENSTLEISTER, ...EXTERNE_ZUSTAENDIGKEITEN],
  },
  { label: STATUS_IMMOBILIENBEZOGEN, details: IMMOBILIENROLLEN },
];

/* ------------------------------------------------------------------ *
 * Übersetzung in die Auswahlliste
 * ------------------------------------------------------------------ */

/** Die Überschrift, unter der die schlichten Einträge stehen: keine. */
export const OHNE_UEBERSCHRIFT = "";

/**
 * Macht aus den Einträgen die flache Liste, die `RollenAuswahl` erwartet.
 *
 * Schlichte Einträge bekommen den leeren `bereich` und landen damit alle in
 * EINEM Block ohne Überschrift — an der Stelle des ersten von ihnen, also
 * oben. Jeder Eintrag mit Details wird ein eigener Block, dessen Überschrift
 * sein Label ist.
 */
export const zuAuswahlOptionen = (eintraege: StatusEintrag[]): VerantOption[] =>
  eintraege.flatMap((e) =>
    e.details
      ? e.details.map((d) => ({ value: d.value, label: d.label, bereich: e.label }))
      : [{ value: e.value as string, label: e.label, bereich: OHNE_UEBERSCHRIFT }],
  );

/** Jeder Wert, den die Firmenliste anbietet — Überschriften zählen nicht mit. */
export const werteDerFirmenliste = (): string[] =>
  zuAuswahlOptionen(STATUS_EINTRAEGE_FIRMA).map((o) => o.value);

/** Dasselbe für die Personenliste. */
export const werteDerPersonenliste = (): string[] =>
  zuAuswahlOptionen(STATUS_EINTRAEGE_PERSON).map((o) => o.value);

/** Das Detail zu einem Wert — oder `undefined`, wenn die Liste ihn nicht kennt. */
export const detailVon = (
  eintraege: StatusEintrag[],
  wert: string,
): StatusDetail | undefined => {
  for (const e of eintraege) {
    for (const d of e.details ?? []) {
      if (d.value === wert) return d;
    }
  }
  return undefined;
};

/**
 * Die Haushalts-Angabe zu einer Rolle, falls sie eine hat.
 *
 * Nur diese Rollen brauchen zusätzlich die Verwandtschaft und werden als
 * Haushaltsbeziehung gespeichert statt als Funktion.
 */
export const haushaltsAngabeVon = (
  eintraege: StatusEintrag[],
  wert: string,
): { verwandtschaft: true; imHaushalt: boolean } | undefined =>
  detailVon(eintraege, wert)?.haushalt;

/** Nachschlagen, ob eine Rolle ein Gegenüber verlangt — und welcher Art. */
export const bezugsArtVon = (
  eintraege: StatusEintrag[],
  wert: string,
): BezugsArt | undefined => {
  for (const e of eintraege) {
    for (const d of e.details ?? []) {
      if (d.value === wert) return d.bezug;
    }
  }
  return undefined;
};

/**
 * Die Frage, die über dem Auswahlfeld steht.
 *
 * Sie ist je Rolle formuliert und nicht generisch („Bezugskontakt"): Wer
 * „Verwalter" angehakt hat, soll lesen „Verwalter — für wen?" und nicht erst
 * überlegen, was ein Bezugskontakt ist.
 */
export const bezugsFrage = (rolle: string, art: BezugsArt): string => {
  if (art === "firma") return `${rolle} — für welche Firma?`;
  if (art === "person") return `${rolle} — für welche Person?`;
  return `${rolle} — für wen?`;
};
